import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { useTranslation } from 'next-i18next'
import Image, { StaticImageData } from 'next/image'
import Link from 'next/link'
import React from 'react'
import ConfigurableAnimatedSection from 'src/components/AnimatedSection'
import { PillarPage } from 'src/constants/searchPages'
import { RedirectUrls } from '../../types/types'

type ICard = {
  title: string
  subtitle: string
  picture: StaticImageData | string
  shadow?: boolean
  alt: string
  search: string
}

type IProps = {
  item: ICard
  index: number
}

/** Cards below this index load eagerly — the section sits under the size
 *  guide's recommended rail, so only the first row is ever near the fold. */
const EAGER_CARD_COUNT = 2

/**
 * One space type per tile: a hairline image window (no radius, no shadow),
 * an italic Fraunces index numeral and the title in the display face. The
 * numeral gives the row a reading order instead of four identical cards.
 */
const ServiceCard = ({ item, index }: IProps) => {
  const { title, subtitle, picture, alt, search } = item
  const page =
    Object.values(PillarPage).find((pillar) => pillar === search) ??
    PillarPage.Parking

  return (
    <li>
      <Link
        id={`${title.trim()}${subtitle.trim()}`}
        href={page}
        className="group flex h-full flex-col border border-ink-100/15 bg-white-500 transition-colors duration-200 hover:border-ink-100/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
      >
        <div className="relative aspect-4/3 w-full overflow-hidden bg-cream-300">
          <Image
            src={picture}
            alt={alt}
            fill
            loading={index < EAGER_CARD_COUNT ? 'eager' : 'lazy'}
            sizes="(min-width: 900px) 22vw, 50vw"
            className="object-cover transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none"
          />
        </div>
        <div className="flex flex-1 flex-col gap-1 p-3 md:p-5">
          <span
            aria-hidden="true"
            className="font-display text-[12px] text-blue-500 italic md:text-[13px]"
          >
            {String(index + 1).padStart(2, '0')}
          </span>
          <h3 className="font-display text-[16px] leading-tight font-medium wrap-break-word text-ink-700 md:text-[20px]">
            {title}
          </h3>
          <p className="hidden text-[14px] leading-relaxed text-ink-200 md:block">
            {subtitle}
          </p>
        </div>
      </Link>
    </li>
  )
}

const ServicesSection = ({
  ctaHref,
  className
}: {
  ctaHref?: string
  className?: string
}) => {
  const { t } = useTranslation('landing')
  const CARDS = t('INDEX_SERVICES.CARDS', { returnObjects: true })

  return (
    <ConfigurableAnimatedSection
      variant="slideUp"
      className={`w-full px-5 py-14 md:px-10 md:py-20 ${className ?? ''}`}
    >
      <div className="mx-auto w-full max-w-7xl">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 md:gap-12">
          <h2 className="max-w-md font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:col-span-5 md:text-[40px]">
            {t('INDEX_SERVICES.HEADING')}
          </h2>
          {/* Text then element — no text→text boundary (Trigger H). */}
          <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-7 md:self-end md:text-[15px]">
            {`${t('INDEX_SERVICES.SUBHEADING_FR_1')} `}
            <span className="font-display font-medium text-ink-700 italic">
              {t('INDEX_SERVICES.SUBHEADING_FR_2')}
            </span>
          </p>
        </div>

        <ul className="mt-10 grid grid-cols-2 gap-3 md:mt-14 md:grid-cols-4 md:gap-5">
          {CARDS.map((card, index) => (
            <ServiceCard key={card.title} item={card} index={index} />
          ))}
        </ul>

        <div className="mt-10 flex border-t border-ink-100/15 pt-8 md:justify-end">
          <Link
            href={ctaHref ?? RedirectUrls.Search}
            className="group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-blue-500 px-7 py-3 text-center text-[15px] font-semibold text-white-500 transition-[background-color,transform] duration-200 hover:bg-blue-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700 active:scale-[0.98] md:w-auto"
          >
            {t('INDEX_SERVICES.BUTTON_SEARCH')}
            <ArrowForwardRoundedIcon className="text-[18px] transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </ConfigurableAnimatedSection>
  )
}

export default React.memo(ServicesSection)
